import type { ReactNode } from 'react';
import { pervyyDostupnyyRazdel } from '../../components/layout/menyuAdminki';
import { NoAccessibleSections } from '../../components/layout/NoAccessibleSections';
import { OtkazVDostupe } from './OtkazVDostupe';
import { OTKAZ_PODPIS, OTKAZ_ZAGOLOVOK } from './otkaz';
import { usePermissions } from './usePermissions';
import { useAuth } from './useAuth';

interface Props {
  perm: string;
  children: ReactNode;
}

/**
 * RequirePermission — роут-гейт по конкретному праву (spatie permission из
 * /auth/me). Зеркало RequireSuperAdmin, но по can(perm): раздел закрыт
 * fail-closed даже прямой ссылкой. Используется поверх RequireAuth.
 *
 * ⛔ ОТКАЗ ОБЪЯСНЯЕТСЯ СЛОВАМИ, А НЕ УВОДИТ МОЛЧА (`s014`, 10.09.2026).
 *
 * До сегодня гейт права делал `<Navigate>` на первый доступный раздел: человек
 * набирал адрес и оказывался на чужом экране без единого слова о том, что
 * произошло. Соседний гейт — видимости (`RequireVidimost`) — ровно в том же
 * случае объясняет отказ, потому что молчаливый редирект признан багом ещё на
 * `s203`. Два ответа на один вопрос «почему я тут» разошлись, и разница стала
 * заметна на экранах записи: `s014` закрыл их правом, и первый же набранный
 * руками `/zapis/den` увёл директолога на «Лиды с форм» молча.
 *
 * Человеку, у которого не открыто НИЧЕГО, по-прежнему отвечает
 * `NoAccessibleSections` — его случай другой: ему некуда идти вовсе.
 */
export function RequirePermission({ perm, children }: Props) {
  const { isLoading } = useAuth();
  const { can, isSuperAdmin, sectionHidden } = usePermissions();

  if (isLoading) {
    return (
      <div style={{ minHeight: '50vh', display: 'grid', placeItems: 'center', color: '#683DFF' }}>
        Загрузка…
      </div>
    );
  }

  if (!can(perm)) {
    // Человеку, которому не открыт ни один раздел, отвечает свой экран: увести
    // его некуда, и «попросите открыть раздел» звучало бы издёвкой.
    const estKudaIdti = pervyyDostupnyyRazdel(can, isSuperAdmin, sectionHidden);

    if (!estKudaIdti) {
      return <NoAccessibleSections />;
    }

    return (
      // ⛔ Слово отказа — одно на всю панель (`s406`, В-14): см. `./otkaz`.
      <OtkazVDostupe zagolovok={OTKAZ_ZAGOLOVOK} kuda={estKudaIdti}>
        {OTKAZ_PODPIS}
      </OtkazVDostupe>
    );
  }

  return <>{children}</>;
}
